// hooks/codes/useNestCode.js
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { normalizeId } from '../../utils/idNormalization';
import { createCodeCacheUtils } from '../../utils/codeCacheUtils';
import { codeKeys } from '../queries';
import QualAPI from 'utils/QualAPI';
import { useOptimisticPromise } from 'hooks/optimistic/useOptimisticPromise';

import { 
  updatePositionsInParent,
  removeCodeFromParent, 
  makeRoomInParent,
  updateMovedCode
} from 'utils/codePositionUtils';

export default function useNestCode() {
  const queryClient = useQueryClient();
  const cacheUtils = createCodeCacheUtils(queryClient);
  const { waitForPromise } = useOptimisticPromise();
  
  return useMutation({
    mutationFn: async ({ codeId, parent_id, position = 0, trackingContext }) => {
      const resolvedCodeId = await waitForPromise(codeId);
      const resolvedParentId = parent_id ? await waitForPromise(parent_id) : null;

      return QualAPI.nestCode(resolvedCodeId, resolvedParentId, position, trackingContext)
        .then(response => response.data);
    },
    
    onMutate: async (variables) => {
      const { codeId, parent_id, position = 0 } = variables;
      const normalizedCodeId = normalizeId(codeId);
      const normalizedParentId = parent_id ? normalizeId(parent_id) : null;
      
      // Get current code to know its current parent and position
      const currentCode = queryClient.getQueryData(codeKeys.detail(normalizedCodeId));
      if (!currentCode) return {};
      
      const projectId = normalizeId(currentCode.project_id);
      const projectCodesKey = codeKeys.list(projectId);
      const projectCodes = queryClient.getQueryData(projectCodesKey);
      
      if (!projectCodes?.codes) return {};
      
      // Cancel queries
      await cacheUtils.cancelQueries(normalizedCodeId);
      
      // We don't need a snapshot since we'll invalidate on error
      
      // Apply the same logic as your Redux reducers
      const originalParentId = currentCode.parent_id || null;
      const originalPosition = currentCode.position || 1;
      
      // 1. Remove gaps in old parent (shift positions down)
      let updatedCodes = removeCodeFromParent(projectCodes.codes, originalParentId, originalPosition);
      
      // 2. Make room in new parent (shift positions up)
      updatedCodes = makeRoomInParent(updatedCodes, normalizedParentId, position);
      
      // 3. Update the moving code itself
      updatedCodes = updateMovedCode(updatedCodes, normalizedCodeId, normalizedParentId, position);
      
      // Update caches
      queryClient.setQueryData(projectCodesKey, {
        ...projectCodes,
        codes: updatedCodes
      });
      
      // Update individual code caches for all codes that had position changes
      updatedCodes.forEach(code => {
        const originalCode = projectCodes.codes.find(orig => normalizeId(orig.id) === normalizeId(code.id));
        
        // Only update if position actually changed
        if (originalCode && originalCode.position !== code.position) {
          cacheUtils.updateCode(normalizeId(code.id), { 
            position: code.position,
            ...(normalizeId(code.id) === normalizedCodeId ? { parent_id: normalizedParentId } : {})
          }, { skipProjectList: true });
        }
      });
      
      return { projectId, normalizedCodeId };
    },
    onSuccess: (data) => {
      // Update caches with the server response to ensure correct state
      // This handles the race condition where useCreateCode might overwrite optimistic nesting
      // Exclude color to avoid overwriting recent color changes
      if (data?.id) {
        const { color, ...dataWithoutColor } = data;
        cacheUtils.updateCode(data.id, dataWithoutColor);
      }
    },
    onError: (err, variables, context) => {
      // Simply invalidate the codes list to refetch fresh data
      if (context?.projectId) {
        queryClient.invalidateQueries({ queryKey: codeKeys.list(context.projectId) });
      }
      console.error('Error nesting code', err);
    }
  });
}